import {
	ClientPortfolioDomainEnum,
	type ClientPropertyTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { isServiceOrAdvisoryDomain } from "web_myplann/Portfolio/Helpers/isServiceOrAdvisoryDomain";
import { portfolioDomainRegistry } from "web_myplann/Portfolio/Helpers/portfolioDomainRegistry";
import { propertyTypeRegistry } from "web_myplann/Portfolio/Helpers/propertyTypeRegistry";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";

export type PortfolioMenuTarget =
	| {
			kind: PortfolioItemKind.Property;
			id: number;
			propertyType: ClientPropertyTypeEnum;
	  }
	| {
			kind: PortfolioItemKind.Portfolio;
			id: number;
			domain: ClientPortfolioDomainEnum;
	  };

type PortfolioMenuActions = {
	editPath: string | undefined;
	showRequestEdit: boolean;
	showUpdateValue: boolean;
	showDelete: boolean;
};

const isPaidAdvisory = (target: PortfolioMenuTarget): boolean =>
	target.kind === PortfolioItemKind.Portfolio &&
	target.domain === ClientPortfolioDomainEnum.PaidAdvisory;

const isEditAllowed = (
	target: PortfolioMenuTarget,
	allowEditByClient: boolean,
): boolean => allowEditByClient && !isPaidAdvisory(target);

const resolveEditPath = (target: PortfolioMenuTarget): string | undefined =>
	target.kind === PortfolioItemKind.Property
		? propertyTypeRegistry[target.propertyType]?.editRoute(target.id)
		: portfolioDomainRegistry[target.domain]?.editRoute(target.id);

export const resolveEditCopy = (
	target: PortfolioMenuTarget,
	showRequestEdit: boolean,
) => {
	if (showRequestEdit) {
		return Resources.Portfolio.Actions.requestEdit;
	}

	if (target.kind === PortfolioItemKind.Property) {
		return Resources.Portfolio.Actions.editProperty;
	}

	return isServiceOrAdvisoryDomain(target.domain)
		? Resources.Portfolio.Actions.editService
		: Resources.Portfolio.Actions.editContract;
};

export const resolvePortfolioMenuActions = (
	target: PortfolioMenuTarget,
	{
		allowEditByClient,
		hasUpdateValue,
	}: { allowEditByClient: boolean; hasUpdateValue: boolean },
): PortfolioMenuActions => {
	const editAllowed = isEditAllowed(target, allowEditByClient);

	return {
		editPath: editAllowed ? resolveEditPath(target) : undefined,
		showRequestEdit: !allowEditByClient,
		showUpdateValue: hasUpdateValue && !isPaidAdvisory(target),
		showDelete: editAllowed,
	};
};
